import React, { useState } from 'react';
import { supabase } from '../lib/db.ts';
import { logger } from '../services/logger.ts';

interface GoogleSignInButtonProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
  className?: string;
}

export function GoogleSignInButton({ onSuccess, onError, className = '' }: GoogleSignInButtonProps) {
  const [loading, setLoading] = useState(false);

  const handleGoogleSignIn = async () => {
    setLoading(true);

    try {
      logger.info('Google Sign-In initiated');

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/auth/verify`
        }
      });

      if (error) {
        logger.error('Google Sign-In error:', error);

        // Provide more specific error messages
        let errorMessage = 'Failed to sign in with Google';

        if (error.message?.includes('invalid_client')) {
          errorMessage = 'Google Sign-In is not properly configured. Please contact support.';
        } else if (error.message?.includes('redirect_uri_mismatch')) {
          errorMessage = 'Redirect URL mismatch. Please try again or contact support.';
        } else if (error.message) {
          errorMessage = error.message;
        }

        alert(errorMessage);
        onError?.(errorMessage);
        return;
      }

      if (data) {
        logger.info('Google Sign-In successful, redirecting...');
        onSuccess?.();
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
      logger.error('Google Sign-In unexpected error:', err);
      alert(errorMessage);
      onError?.(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      onClick={handleGoogleSignIn}
      disabled={loading}
      className={`
        w-full flex items-center justify-center gap-3 px-4 py-2 
        bg-white text-black border border-gray-300 rounded-md font-medium
        hover:bg-gray-50 transition-colors duration-200
        disabled:opacity-50 disabled:cursor-not-allowed
        ${className}
      `}
    >
      {/* Google Logo SVG */}
      <svg className="w-5 h-5" viewBox="0 0 48 48" aria-hidden="true">
        <path fill="#EA4335" d="M24 9.5c3.5 0 6.3 1.2 8.3 3.3l6.2-6.2C34.8 2.2 29.7 0 24 0 14.6 0 6.6 5.5 2.7 13.5l7.6 5.9C12 13 17.5 9.5 24 9.5z"/>
        <path fill="#34A853" d="M46.5 24.5c0-1.6-.2-3.1-.5-4.5H24v9.5h12.6c-.5 2.7-1.9 5-4 6.6l6.3 4.9c3.7-3.4 5.9-8.4 5.9-14.5z"/>
        <path fill="#FBBC05" d="M10.3 28.2c-.5-1.5-.8-3.1-.8-4.7s.3-3.2.8-4.7l-7.6-5.9C1 16.7 0 20.2 0 24s1 7.3 2.7 10.2l7.6-6z"/>
        <path fill="#4285F4" d="M24 48c6.5 0 12-2.2 16-6l-6.3-4.9c-2.2 1.5-5 2.4-9.7 2.4-6.5 0-12-4.3-13.9-10.1l-7.6 6C6.6 42.5 14.6 48 24 48z"/>
        <path fill="none" d="M0 0h48v48H0z"/>
      </svg>

      <span>
        {loading ? 'Signing in...' : 'Sign in with Google'}
      </span>
    </button>
  );
}
